import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {currentResource, labels} from '../../../util/utils';
import {groupRiskStatus} from '../../../util/uiconstants';

import {IconoirInfoCircle, IconoirWarningTriangle} from '@ey-xd/motif-icon';
import OutputText from '@ey/canvascoreservices/OutputText';

import selectComponentRisksFromRelatedRisks from '../../../reducers/Reconciliations/selectors/selectComponentRisksFromRelatedRisks';
import selectProjectAndEngIds from '../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';

import {clearErrors, raiseSuccessMessage} from '../../../actions/erroractions';
import {updateGroupComponentRisk, updateReconciliationRisk} from '../../../actions/Reconciliations';

import LoadingWrapper from '../../CoreWrappers/LoadingWrapper';
import ShowMoreLess from '../../CoreWrappers/ShowMoreLess';
import IconMessage from '../../CoreWrappers/IconMessage';
import Accordion from '../../CoreWrappers/Accordion';
import Button from '../../CoreWrappers/Button';
import HrVert from '../Hr/HrVert';
import HrAlt from '../Hr/HrAlt';
import AccountAndRiskDetailsFooterMessage from '../AccountAndRiskDetailsFooterMessage';
import RelatedAccountsToRiskTable from './RelatedAccountsToRisksTable';
import MessageWithTrigger from '../MessageWithTrigger';
import DeleteCommentModal from '../DeleteCommentModal';
import RelateRisksModal from './RelateRisksModal';
import RiskNameDisplay from '../RiskNameDisplay';
import CommentModal from '../CommentModal';
import StyledCommentHeader from '../AccountDetails/StyledCommentHeader';
import StyledRiskAccordionHeader from './StyledRiskAccordionHeader';
import StyledComponentRiskHeader from './StyledComponentRiskHeader';
import StyledAccordionContent from './StyledAccordionContent';
import StyledRiskDetailsName from './StyledRiskDetailsName';
import StyledRiskDetails from './StyledRiskDetails';

const RiskDetails = ({primaryRisk = null}) => {
	const isOpenStatus = primaryRisk.groupRiskStatusId === groupRiskStatus.open || primaryRisk.groupRiskStatusId === groupRiskStatus.systemReOpened;
	const [open, setOpen] = useState(isOpenStatus);
	const [isLoading, setIsLoading] = useState(false);
	const [relateModalShow, setRelateModalShow] = useState(false);

	if (!primaryRisk) return false;

	const {relatedPrimaryRisks = []} = primaryRisk;

	const relatedComponentRisks = selectComponentRisksFromRelatedRisks(relatedPrimaryRisks);
	const apiConfig = selectProjectAndEngIds();

	const dispatch = useDispatch();

	const hasMappedRisks = relatedComponentRisks.length > 0;

	const hrColor = '--neutrals-200';

	const hasGroupComponentRiskWithRationale = primaryRisk.groupComponentId && primaryRisk.rationale;

	const renderRiskTypeAndName = ({riskTypeId, riskName}, multiline = 0) => (
		<StyledRiskDetailsName>
			<RiskNameDisplay typeId={riskTypeId} name={riskName} multiline={multiline} />
		</StyledRiskDetailsName>
	);

	const renderAccordionHeader = () => {
		return (
			<StyledRiskAccordionHeader>
				{renderRiskTypeAndName(primaryRisk)}
				{!hasGroupComponentRiskWithRationale && renderCommentModal()}
			</StyledRiskAccordionHeader>
		);
	};

	const renderAccountsTable = ({riskId, accounts = []}, isPrimary = false) => {
		const props = {
			id: `table_${riskId}`,
			className: 'primary-table',
			data: accounts.map((account) => ({...account, id: account.accountId})),
			isPrimary
		};

		return <RelatedAccountsToRiskTable {...props} />;
	};

	const renderTrigger = (label = labels.relateRisks, size = 'small', onClick = null) => {
		const props = {
			variant: 'text-alt',
			label,
			size
		};

		if (onClick) props.onClick = onClick;

		return <Button {...props} />;
	};

	const renderRelateRisksModalTrigger = () => {
		const props = {
			variant: 'text-alt',
			size: 'small',
			label: labels.relateRisks,
			title: labels.relateRisks,
			onClick: () => setRelateModalShow(true)
		};

		return <Button {...props} />;
	};

	const renderMappedRisksPlaceholder = () => {
		const props = {
			className: 'empty-message',
			iconColor: '--orange-600',
			icon: IconoirWarningTriangle,
			noBackground: true,
			title: null,
			subTitle: <MessageWithTrigger message={labels.relateRisksMessage} renderTrigger={renderRelateRisksModalTrigger} />
		};

		return <IconMessage {...props} />;
	};

	const renderMappedRisks = () => {
		const [firstRisk, ...otherRisks] = relatedComponentRisks;

		return (
			<>
				<div className="first-component-header">
					<StyledComponentRiskHeader>
						<OutputText body2>{labels.componentTeamRisksAndRelatedAccounts}</OutputText>
						{isOpenStatus && renderRelateRisksModalTrigger()}
					</StyledComponentRiskHeader>
					<HrAlt $color={hrColor} />
					{renderRiskTypeAndName(firstRisk, 3)}
				</div>
				<div className="first-component-table">{renderAccountsTable(firstRisk)}</div>
				<div className="rest-component-risks">
					{otherRisks.map((risk) => {
						return (
							<>
								{renderRiskTypeAndName(risk, 3)}
								{renderAccountsTable(risk)}
							</>
						);
					})}
				</div>
			</>
		);
	};

	const getFooterMessage = () => {
		if(primaryRisk.groupRiskStatusId === groupRiskStatus.open) return labels.riskMarkAssessedMessage;
		if(primaryRisk.groupRiskStatusId === groupRiskStatus.systemReOpened) return labels.riskWasPreviousAssessedMessage;
		return labels.riskMarkOpenMessage;
	}

	const renderFooterInfo = () => {
		const successMessage = isOpenStatus ? labels.successAssessedMessage : labels.successReOpenMessage;
		const triggerLabel = isOpenStatus ? labels.markAssessed : labels.markReOpen;
		const footerMessage = getFooterMessage();

		const updateStatus = () => {
			setIsLoading(true);
			const payload = {
				groupRiskStatusId: isOpenStatus ? groupRiskStatus.assessed : groupRiskStatus.open
			};
			dispatch(updateReconciliationRisk(primaryRisk.riskId, payload, apiConfig))
				.then(() => {
					setIsLoading(false);
					dispatch(raiseSuccessMessage(successMessage)).then(() =>
						setTimeout(() => {
							dispatch(clearErrors());
						}, 5000)
					);
				})
				.catch(() => {
					setIsLoading(false);
				});
		};

		const props = {
			message: footerMessage,
			renderTrigger: () => renderTrigger(triggerLabel, 'small', updateStatus),
			icon: IconoirInfoCircle,
			color: 'black'
		};

		return (
			<div className="footer">
				<HrAlt $color={hrColor} />
				<AccountAndRiskDetailsFooterMessage {...props} />
			</div>
		);
	};

	const handleSaveComment = (rationale) => {
		const payload = {
			rationale,
			concurrencyToken: primaryRisk.concurrencyToken
		};

		return dispatch(updateGroupComponentRisk(primaryRisk.riskId, primaryRisk.groupComponentRiskId, payload, apiConfig));
	};

	const renderCommentModal = (isEdit = false) => {
		const props = {
			isEdit: isEdit,
			rationale: primaryRisk.rationale,
			rationaleModifyDate: primaryRisk.rationaleModifyDate,
			rationaleModifyBy: primaryRisk.rationaleModifyBy,
			onSaveComment: handleSaveComment
		};

		return (
			<div onClick={(e) => e.stopPropagation()} className="create-comment-container">
				<CommentModal {...props} />
			</div>
		);
	};

	const renderEditComment = () => {
		return (
			<div className="comment-wrapper">
				<HrAlt $color="--neutrals-200" />
				<StyledCommentHeader>
					<OutputText body2 left>
						{labels.commentTitle}
					</OutputText>
					{renderCommentModal(true)}
					{renderDeleteCommentModal()}
				</StyledCommentHeader>
				{open && <ShowMoreLess text={primaryRisk.rationale} numLines={3} />}
			</div>
		);
	};

	const renderDeleteCommentModal = () => {
		const props = {
			labels: currentResource.deleteCommentModalLabels,
			onSave: handleSaveComment
		};

		return <DeleteCommentModal {...props} />;
	};

	const props = {
		open,
		onOpen: () => setOpen(true),
		onClose: () => setOpen(false),
		trigger: renderAccordionHeader
	};

	return (
		<StyledRiskDetails>
			<Accordion {...props}>
				<LoadingWrapper isLoading={isLoading}>
					<StyledAccordionContent className={classNames({mapped: hasMappedRisks})}>
						<div className="primary-header">
							<OutputText body2>{labels.primaryTeamAccountsRelatedToRisk}</OutputText>
							<HrAlt $color={hrColor} />
						</div>
						{renderAccountsTable(primaryRisk, true)}
						<HrVert className="separator" $color={hrColor} />
						{hasMappedRisks && renderMappedRisks()}
						{!hasMappedRisks && renderMappedRisksPlaceholder()}
						{hasGroupComponentRiskWithRationale && renderEditComment()}
						{hasMappedRisks && renderFooterInfo()}
						<RelateRisksModal primaryRisk={primaryRisk} setShow={setRelateModalShow} show={relateModalShow} />
					</StyledAccordionContent>
				</LoadingWrapper>
			</Accordion>
		</StyledRiskDetails>
	);
};

const riskShape = {
	riskId: PropTypes.string.isRequired,
	riskName: PropTypes.string,
	riskTypeId: PropTypes.oneOf([1, 2, 3]),
	assertions: PropTypes.arrayOf(
		PropTypes.shape({
			assertionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
			assertionAbbreviation: PropTypes.string,
			isHigherRisk: PropTypes.bool
		})
	)
};

RiskDetails.propTypes = {
	primaryRisk: PropTypes.shape({...riskShape, relatedAccounts: PropTypes.shape({...riskShape})})
};

export default RiskDetails;
